
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute } from 'vue-router'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import CompaniesTabs from '@/modules/CompaniesModule/components/CompaniesTabs/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'

// types
import {
    CompanyActivityItem,
    CompanyScheduleT,
    CompanySetting,
    CompanyT,
    CompanyContractT
} from '@/core/types/companies.types'

// constants
import { WORK_WEEK_DAYS } from '@/core/constants/companies.constants'

// composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// realisations
import { companies } from '@/core/realisations/companies.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// constants
import { ContractsStatusColors } from '@/core/constants/columns/Contracts.columns'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

export const NotificationsColumns = [
    {
        name: 'ID',
        key: 'id',
    },
    {
        name: 'Описание',
        key: 'description',
    },
]

export const ContractsColumns = [
    {
        name: '№ Договора',
        key: 'number'
    },
    {
        name: 'Тип',
        key: 'type'
    },
    {
        name: 'Дата заключения',
        key: 'date'
    },
    {
        name: 'Дата окончания',
        key: 'expiration_date'
    },
    {
        name: 'Услуги',
        key: 'services'
    },
    {
        name: 'Статус',
        key: 'status'
    }
]

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Loader,
        Cheap,
        CompaniesTabs,
        TheInput,
        TheTable,
        FailedFetch,
        TableScrollWrapper,
    },
    setup() {
        const route = useRoute()
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const company_id = Number(route.params.company_id as string)

        const state = reactive({
            is_loading: false,
            is_failed: false,
            company: null as CompanyT | null,
            currentTab: 'general',
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
        })

        const contracts_state = reactive({
            is_loading: false,
            is_failed: false,
            contracts: [] as Array<CompanyContractT>
        })

        const settings_state = reactive({
            is_manage_loading: false,
            settings: new DataState<CompanySetting>({}),
            limit: 50,
        })

        const schedules_state = reactive({
            is_loading: false,
            is_failed: false,
            schedules: [] as CompanyScheduleT[],
        })

        const activity_state = reactive({
            is_loading: false,
            is_failed: false,
            activities: [] as CompanyActivityItem[],
        })

        const loadCompany = async () => {
            try {
                state.is_loading = true

                const response = await companies.getCompany(company_id)
                state.company = response.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении данных компании произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadContracts = async () => {
            try {
                contracts_state.is_loading = true

                const response = await companies.getCompanyContracts(company_id)
                contracts_state.contracts =response.data

                contracts_state.is_failed = false
            } catch (error) {
                contracts_state.is_failed = true
                console.error(error)
                notifications.notifyError({ error, title: 'При получении договоров произошла ошибка' })
            } finally {
                contracts_state.is_loading = false
            }
        }

        const loadSettings = async (page: number) => {
            settings_state.settings.pagination.current_page = page

            try {
                settings_state.settings.is_loading = true

                const response = await companies.getCompanySettings(company_id, { page, limit: settings_state.limit })
                settings_state.settings.list = response.data

                settings_state.settings.pagination.total_pages = response.meta.last_page
                settings_state.settings.pagination.total_items = response.meta.total

                settings_state.settings.is_failed = false
            } catch (error) {
                settings_state.settings.failure.catchError(error)
                settings_state.settings.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении настроек компании произошла ошибка',
                })
            } finally {
                settings_state.settings.is_loading = false
            }
        }

        const loadCompanySchedule = async () => {
            try {
                schedules_state.is_loading = true

                const response = await companies.getSchedules({ companyId: company_id })
                schedules_state.schedules = response.data

                schedules_state.is_failed = false
            } catch (error) {
                schedules_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении графика работы компании произошла ошибка',
                })
            } finally {
                schedules_state.is_loading = false
            }
        }

        const loadCompanyActivity = async () => {
            try {
                activity_state.is_loading = true

                const response = await companies.getActivity(
                    company_id,
                    state.createdAtFrom,
                    state.createdAtTo
                )
                activity_state.activities = response.data

                activity_state.is_failed = false
            } catch (error) {
                activity_state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении истории изменений компании произошла ошибка',
                })
            } finally {
                activity_state.is_loading = false
            }
        }

        const reset = () => {
            state.createdAtFrom = null
            state.createdAtTo = null

            loadCompanyActivity()
        }

        onBeforeMount(() => {
            loadCompany()
            loadCompanySchedule()
            loadCompanyActivity()
            loadSettings(1)
            loadContracts()
        })

        //

        const isGeneral = computed(() => state.currentTab === 'general')
        const isChat = computed(() => state.currentTab === 'chat')
        const isContacts = computed(() => state.currentTab === 'contact')
        const isSettings = computed(() => state.currentTab === 'settings')

        const isHaveAccountant = computed(() => {
            if (!state.company) return false
            if (!state.company.accountant) return false

            return true
        })

        const senderSchedules = computed(() => {
            if (!schedules_state.schedules.length) return []
            return schedules_state.schedules.filter((schedule) => schedule.scheduleType.id === 15)
        })

        const receiverSchedules = computed(() => {
            if (!schedules_state.schedules.length) return []
            return schedules_state.schedules.filter((schedule) => schedule.scheduleType.id === 16)
        })

        const getCompanyTitle = computed(() => {
            return state.company
                ? `${state.company.name} (ID: ${company_id})`
                : `(ID: ${company_id})`
        })

        const getStatusClass = (status_id: number) => {
            let status_class = 'ml-3 small'

            switch (status_id) {
                case 2:
                    status_class += ' green'
                    break
                case 3:
                    status_class += ' red'
                    break
            }

            return status_class
        }

        const getCountText = (count: number) => {
            const countStr = count.toString()
            const countLast = Number(countStr[countStr.length - 1])

            if (countLast === 1) return 'событие'
            else if (2 <= countLast && countLast <= 4) return 'события'
            else return 'событий'
        }

        const getActiveBtnText = (row: CompanySetting) => {
            return row.isActive ? 'Выключить' : 'Включить'
        }

        const enableSetting = async (row: CompanySetting) => {
            const answer = confirm('Вы действительно хотите включить настройку?')
            if (!answer) return

            try {
                settings_state.is_manage_loading = true
                await companies.enableSetting(company_id, row.settingId)
                loadSettings(1)
                notifications.notify({
                    title: 'Настройка компании была включена!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При включении настройки произошла ошибка',
                })
            } finally {
                settings_state.is_manage_loading = false
            }
        }

        const disableSetting = async (row: CompanySetting) => {
            const answer = confirm('Вы действительно хотите выключить настройку?')
            if (!answer) return

            try {
                settings_state.is_manage_loading = true
                await companies.disableSetting(company_id, row.settingId)
                loadSettings(1)
                notifications.notify({
                    title: 'Настройка компании была выключена!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При выключении настройки произошла ошибка',
                })
            } finally {
                settings_state.is_manage_loading = false
            }
        }

        const manageActiveBtn = (row: CompanySetting) => {
            if (row.isActive) disableSetting(row)
            else enableSetting(row)
        }

        const onLimitChange = (limit: number) => {
            settings_state.limit = limit
            loadSettings(settings_state.settings.pagination.current_page)
        }

        const onChangePage = (page: number) => {
            settings_state.settings.pagination.current_page = page
            loadSettings(page)
        }

        const isCanManageSettings = computed(() => havePermissions(['company.settings-update']))

        const isCompanyTimeSlotsEnabled = computed(() => {
            if (state.company) {
                if (state.company.companySettings.length) {
                    return state.company.companySettings.filter((s) => s.isActive).map((s) => s.settingId).includes(32)
                }
            }

            return false
        })

        return {
            state,
            schedules_state,
            activity_state,
            settings_state,
            contracts_state,
            company_id,
            loadCompany,
            loadCompanySchedule,
            loadCompanyActivity,
            isHaveAccountant,
            WORK_WEEK_DAYS,
            senderSchedules,
            receiverSchedules,
            getCompanyTitle,
            getStatusClass,
            isGeneral,
            isSettings,
            isChat,
            isContacts,
            getCountText,
            reset,
            NotificationsColumns,
            ContractsColumns,
            ContractsStatusColors,
            getActiveBtnText,
            manageActiveBtn,
            isCanManageSettings,
            isCompanyTimeSlotsEnabled,
            loadSettings,
            onLimitChange,
            onChangePage,
            loadContracts,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
